import React, { useState, useEffect } from 'react';
import { login, register, updateApiEnvironment } from '../../services/api';
import socketService from '../../services/socket';
import { 
  environments, 
  getCurrentEnvironment, 
  setCurrentEnvironment 
} from '../../config/environments';
import './Auth.css';

function Auth({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedEnvId, setSelectedEnvId] = useState(getCurrentEnvironment().id);

  // Update API and Socket services when environment changes
  useEffect(() => {
    const env = environments.find((e) => e.id === selectedEnvId);
    if (env) {
      updateApiEnvironment(env);
      socketService.updateEnvironment(env);
      setCurrentEnvironment(env.id);
    }
  }, [selectedEnvId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const data = await login(username, password);
        onLogin(data.user, data.token);
      } else {
        // Register
        if (!email || !username || !password) {
          setError('All fields are required');
          setLoading(false);
          return;
        }
        const data = await register(username, email, password, displayName || username);
        onLogin(data.user, data.token);
      }
    } catch (err) {
      console.error('Auth error:', err);
      
      // Handle specific error types
      if (err.isTimeout) {
        setError('Connection timeout. The server is not responding. Please try a different environment or check your connection.');
      } else if (err.isNetworkError) {
        setError('Unable to connect to server. Please verify the server is running and try again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Authentication failed');
      }
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/fpstak-logo.png" alt="FPSTAK Logo" className="logo-image" />
          </div>
          <h1>FPSTAK</h1>
          <p className="auth-subtitle">First Person Shooter Team Awareness Tool Kit</p>
          <p className="auth-tagline"><em>"See Everything. Miss Nothing."</em></p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="email">EMAIL ADDRESS</label>
              <input
                type="email"
                id="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required={!isLogin}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">USERNAME</label>
            <input
              type="text"
              id="username"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
            />
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="displayName">DISPLAY NAME (OPTIONAL)</label>
              <input
                type="text"
                id="displayName"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="How others see you"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">PASSWORD</label>
            <input
              type="password"
              id="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              minLength={8}
            />
            {!isLogin && (
              <small className="form-hint">Minimum 8 characters required</small>
            )}
          </div>

          <div className="auth-environment">
            <label htmlFor="environment" className="environment-label">
              ENVIRONMENT
            </label>
            <select
              id="environment"
              className="environment-select"
              value={selectedEnvId}
              onChange={(e) => {
                setSelectedEnvId(e.target.value);
                setError(''); // Clear any previous errors
              }}
            >
              {environments.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name}
                </option>
              ))}
            </select>
            <small className="environment-hint">
              Select the server environment to connect to
            </small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? (
              <span className="btn-loading">
                <span className="spinner"></span>
                PROCESSING...
              </span>
            ) : (
              <span>{isLogin ? 'LOGIN' : 'REGISTER'}</span>
            )}
          </button>

          <div className="auth-footer">
            <p className="text-muted">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setUsername('');
                  setEmail('');
                  setPassword('');
                  setDisplayName('');
                }}
              >
                {isLogin ? 'Register here' : 'Login here'}
              </button>
            </p>
          </div>
        </form>
      </div>
      
      <div className="auth-background-pattern"></div>
    </div>
  );
}

export default Auth;

